import { propagation, ROOT_CONTEXT } from "@opentelemetry/api";
import { getSanitizedReferer } from "../web-logger/baggage-mapping";

declare global {
  interface Window {
    __VG_OTEL_WEB__?: {
      registerGlobalAttributes?: (attrs: Record<string, string>) => void;
    };
    __OTEL_BAGGAGE_CONTEXT__?: unknown;
  }
}

/**
 * Initializes OTel baggage and global span attributes after the CDN script has loaded.
 *
 * Baggage values propagated to all downstream services via `traceparent`/`baggage` headers:
 * - cc=UI (call chain origin — identifies browser as the initiator)
 * - cl=<poid> (client POID extracted from cookies)
 * - re=<referer> (sanitized current URL — origin + pathname, no query/hash)
 *
 * Global span attribute:
 * - vg.deployment_mode = "internal" | "external" (filterable in Honeycomb)
 *
 * @param isInternalDeployment - Whether this is an internal (Vanguard employee) deployment
 */
export function initializeOtelBaggage(isInternalDeployment: boolean): void {
  const clientPoid = extractClientPoid();

  const baggageEntries: Record<string, { value: string }> = {
    cc: { value: "UI" },
    re: { value: getSanitizedReferer() },
  };

  if (clientPoid) {
    baggageEntries["cl"] = { value: clientPoid };
  }

  const baggage = propagation.createBaggage(baggageEntries);
  const contextWithBaggage = propagation.setBaggage(ROOT_CONTEXT, baggage);

  // Store context on window for fallback access in Angular HTTP interceptors
  // (Zone.js loses async context, so interceptors read from this global)
  window.__OTEL_BAGGAGE_CONTEXT__ = contextWithBaggage;

  // Register deployment mode as a global attribute on all spans
  if (window.__VG_OTEL_WEB__?.registerGlobalAttributes) {
    window.__VG_OTEL_WEB__.registerGlobalAttributes({
      "vg.deployment_mode": isInternalDeployment ? "internal" : "external",
    });
  }
}

/**
 * Extracts the client POID from browser cookies.
 *
 * Fallback chain (first non-empty value wins):
 * 1. POID cookie (set by Siteminder on external flows)
 * 2. cl_spoid cookie (set by session authorization)
 * 3. cccSharedData cookie (pipe-delimited, POID is the first segment)
 */
function extractClientPoid(): string | null {
  const cookies = document.cookie.split(";");

  const poid = getCookieValue(cookies, "POID");
  if (poid) return poid;

  const sessionPoid = getCookieValue(cookies, "cl_spoid");
  if (sessionPoid) return sessionPoid;

  return extractPoidFromSharedDataCookie(cookies);
}

function getCookieValue(cookies: string[], cookieName: string): string | null {
  const matchingCookie = cookies.find((c) => c.split("=")[0].trim() === cookieName);
  if (!matchingCookie) return null;

  const separatorIndex = matchingCookie.indexOf("=");
  if (separatorIndex === -1) return null;

  const cookieValue = matchingCookie.slice(separatorIndex + 1);
  return cookieValue ? cookieValue.trim() : null;
}

function extractPoidFromSharedDataCookie(cookies: string[]): string | null {
  const matchingCookie = cookies.find((c) => c.split("=")[0].trim() === "cccSharedData");
  if (!matchingCookie) return null;

  const separatorIndex = matchingCookie.indexOf("=");
  if (separatorIndex === -1) return null;

  const rawValue = matchingCookie.slice(separatorIndex + 1);
  if (!rawValue) return null;

  // cccSharedData is pipe-delimited: "POID|field2|field3|..."
  const poidSegment = rawValue.split("|")[0];
  return poidSegment ? poidSegment.trim() : null;
}
